import { BarChart3 } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";

import {
  groupChain,
  optionDailyPercentChange,
  optionPriceDirection,
  optionPriceRoi,
  type OptionPriceDirection,
} from "../chain";
import {
  formatCompact,
  formatGreek,
  formatPercent,
  formatPrice,
  signedClass,
} from "../format";
import { analyticsDisclosure, analyticsTone } from "../optionAnalytics";
import type { OptionContract } from "../types";

interface OptionChainProps {
  contracts: OptionContract[];
  underlyingPrice: number | null;
  selectedCode: string | null;
  loading: boolean;
  onSelect: (contract: OptionContract) => void;
}

function QuoteCell({
  contract,
  field,
  kind = "number",
}: {
  contract?: OptionContract;
  field: keyof OptionContract;
  kind?: "number" | "percent" | "compact" | "greek";
}) {
  const value = contract?.[field];
  const number = typeof value === "number" ? value : null;
  const isAnalyticsField = kind === "greek" || field === "implied_volatility";
  const hasAnalyticsMetadata = Boolean(
    contract?.analytics_status ||
      contract?.analytics_reason ||
      contract?.analytics_model ||
      contract?.analytics_observed_at,
  );
  const tone = analyticsTone(contract?.analytics_status);
  const classes = [
    field === "percent_change" ? signedClass(number) : "",
    isAnalyticsField && number === null && tone !== "healthy"
      ? `analytics-${tone}`
      : "",
  ]
    .filter(Boolean)
    .join(" ");
  const title =
    isAnalyticsField && contract && hasAnalyticsMetadata
      ? analyticsDisclosure({
          status: contract.analytics_status,
          reason: contract.analytics_reason,
          model: contract.analytics_model,
          observedAt: contract.analytics_observed_at,
        })
      : undefined;
  let text = "—";
  if (kind === "percent") text = formatPercent(number);
  else if (kind === "compact") text = formatCompact(number);
  else if (kind === "greek") text = formatGreek(number);
  else text = formatPrice(number);
  return (
    <td className={classes} title={title}>
      {text}
    </td>
  );
}

function optionMoveClass(direction: OptionPriceDirection): string {
  if (direction === "up") return "option-price-up";
  if (direction === "down") return "option-price-down";
  return "";
}

function SignedPercentCell({ contract }: { contract?: OptionContract }) {
  const percentChange = optionDailyPercentChange(contract);
  const text = formatPercent(percentChange);
  return (
    <td className={optionMoveClass(optionPriceDirection(contract))}>
      {percentChange !== null && percentChange > 0 ? `+${text}` : text}
    </td>
  );
}

function RoiCell({
  contract,
  underlyingPrice,
}: {
  contract?: OptionContract;
  underlyingPrice: number | null;
}) {
  return <td>{formatPercent(optionPriceRoi(contract, underlyingPrice))}</td>;
}

function LastPriceCell({
  contract,
  selected,
  chartIconFirst,
  onSelect,
}: {
  contract?: OptionContract;
  selected: boolean;
  chartIconFirst: boolean;
  onSelect: (contract: OptionContract) => void;
}) {
  const classes = [
    selected ? "selected" : "",
    optionMoveClass(optionPriceDirection(contract)),
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <td className={classes}>
      {contract ? (
        <button
          type="button"
          className="chain-price-button"
          onClick={() => onSelect(contract)}
          title={`Chart ${contract.code}`}
        >
          {chartIconFirst && <BarChart3 size={12} />}
          {formatPrice(contract.last ?? contract.mark)}
          {!chartIconFirst && <BarChart3 size={12} />}
        </button>
      ) : (
        "—"
      )}
    </td>
  );
}

export function OptionChain({
  contracts,
  underlyingPrice,
  selectedCode,
  loading,
  onSelect,
}: OptionChainProps) {
  const rows = useMemo(() => groupChain(contracts), [contracts]);
  const scrollContainer = useRef<HTMLDivElement>(null);
  const selectedRow = useRef<HTMLTableRowElement>(null);
  const lastScrolledCode = useRef<string | null>(null);
  const centeredChainKey = useRef<string | null>(null);
  const chainKey = contracts[0]
    ? `${contracts[0].ticker}:${contracts[0].expiration}`
    : null;

  useEffect(() => {
    if (!chainKey || !scrollContainer.current) {
      centeredChainKey.current = null;
      return;
    }
    if (centeredChainKey.current === chainKey) return;

    const container = scrollContainer.current;
    container.scrollLeft = Math.max(
      0,
      (container.scrollWidth - container.clientWidth) / 2,
    );
    centeredChainKey.current = chainKey;
  }, [chainKey, rows]);

  useEffect(() => {
    if (!selectedCode) {
      lastScrolledCode.current = null;
      return;
    }
    if (
      lastScrolledCode.current === selectedCode ||
      !scrollContainer.current ||
      !selectedRow.current
    ) {
      return;
    }

    const container = scrollContainer.current;
    const row = selectedRow.current;
    const stickyHeaderHeight = 58;
    const availableHeight = container.clientHeight - stickyHeaderHeight;
    const top =
      row.offsetTop -
      stickyHeaderHeight -
      Math.max(0, (availableHeight - row.offsetHeight) / 2);
    container.scrollTo({
      top: Math.max(0, top),
      left: container.scrollLeft,
      behavior: "smooth",
    });
    lastScrolledCode.current = selectedCode;
  }, [rows, selectedCode]);

  return (
    <div ref={scrollContainer} className="chain-scroll">
      <table className="option-chain" data-testid="option-chain">
        <colgroup>
          <col className="quote-column" span={12} />
          <col className="strike-column" />
          <col className="quote-column" span={12} />
        </colgroup>
        <thead>
          <tr className="chain-groups">
            <th colSpan={12}>CALLS</th>
            <th className="strike-head" rowSpan={2}>
              STRIKE
            </th>
            <th colSpan={12}>PUTS</th>
          </tr>
          <tr>
            <th title="Vega calculated locally with the disclosed American option model">
              VEGA
            </th>
            <th title="Theta per calendar day, calculated locally">THETA</th>
            <th title="Gamma per $1² move, calculated locally">GAMMA</th>
            <th title="Delta per $1 move, calculated locally">DELTA</th>
            <th title="Implied volatility calculated locally from the OPRA midpoint">
              IV
            </th>
            <th>OI</th>
            <th>VOL</th>
            <th>BID</th>
            <th>ASK</th>
            <th title="Last price divided by current underlying price">ROI</th>
            <th title="Change from previous close">CHG%</th>
            <th>LAST</th>
            <th>LAST</th>
            <th title="Change from previous close">CHG%</th>
            <th title="Last price divided by current underlying price">ROI</th>
            <th>BID</th>
            <th>ASK</th>
            <th>VOL</th>
            <th>OI</th>
            <th title="Implied volatility calculated locally from the OPRA midpoint">
              IV
            </th>
            <th title="Delta per $1 move, calculated locally">DELTA</th>
            <th title="Gamma per $1² move, calculated locally">GAMMA</th>
            <th title="Theta per calendar day, calculated locally">THETA</th>
            <th title="Vega calculated locally with the disclosed American option model">
              VEGA
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const atMoney =
              underlyingPrice !== null &&
              Math.abs(row.strike - underlyingPrice) <=
                Math.max(2.5, underlyingPrice * 0.005);
            const isSelectedRow =
              selectedCode === row.call?.code ||
              selectedCode === row.put?.code;
            return (
              <tr
                key={row.strike}
                ref={isSelectedRow ? selectedRow : null}
                className={atMoney ? "at-money" : ""}
                data-strike-cents={Math.round(row.strike * 100)}
              >
                <QuoteCell contract={row.call} field="vega" kind="greek" />
                <QuoteCell contract={row.call} field="theta" kind="greek" />
                <QuoteCell contract={row.call} field="gamma" kind="greek" />
                <QuoteCell contract={row.call} field="delta" kind="greek" />
                <QuoteCell
                  contract={row.call}
                  field="implied_volatility"
                  kind="percent"
                />
                <QuoteCell
                  contract={row.call}
                  field="open_interest"
                  kind="compact"
                />
                <QuoteCell contract={row.call} field="volume" kind="compact" />
                <QuoteCell contract={row.call} field="bid" />
                <QuoteCell contract={row.call} field="ask" />
                <RoiCell
                  contract={row.call}
                  underlyingPrice={underlyingPrice}
                />
                <SignedPercentCell contract={row.call} />
                <LastPriceCell
                  contract={row.call}
                  selected={selectedCode === row.call?.code}
                  chartIconFirst
                  onSelect={onSelect}
                />
                <th scope="row" className="strike-cell">
                  {row.strike.toFixed(2)}
                </th>
                <LastPriceCell
                  contract={row.put}
                  selected={selectedCode === row.put?.code}
                  chartIconFirst={false}
                  onSelect={onSelect}
                />
                <SignedPercentCell contract={row.put} />
                <RoiCell
                  contract={row.put}
                  underlyingPrice={underlyingPrice}
                />
                <QuoteCell contract={row.put} field="bid" />
                <QuoteCell contract={row.put} field="ask" />
                <QuoteCell contract={row.put} field="volume" kind="compact" />
                <QuoteCell
                  contract={row.put}
                  field="open_interest"
                  kind="compact"
                />
                <QuoteCell
                  contract={row.put}
                  field="implied_volatility"
                  kind="percent"
                />
                <QuoteCell contract={row.put} field="delta" kind="greek" />
                <QuoteCell contract={row.put} field="gamma" kind="greek" />
                <QuoteCell contract={row.put} field="theta" kind="greek" />
                <QuoteCell contract={row.put} field="vega" kind="greek" />
              </tr>
            );
          })}
        </tbody>
      </table>
      {loading && <div className="table-state">Loading option chain…</div>}
      {!loading && rows.length === 0 && (
        <div className="table-state">Waiting for the first chain snapshot</div>
      )}
    </div>
  );
}
